import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';

const clamp = (s) => Math.min(5, Math.max(1, s));

export const ImageLightbox = ({ src, alt = 'photo', onClose }) => {
  const [scale, setScale] = useState(1);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const pointers = useRef(new Map());
  const gesture = useRef(null);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { window.removeEventListener('keydown', onKey); document.body.style.overflow = prev; };
  }, [onClose]);

  const applyScale = (s, next) => {
    const v = clamp(s);
    setScale(v);
    if (v === 1) setPos({ x: 0, y: 0 });
    else if (next) setPos(next);
  };

  const onPointerDown = (e) => {
    e.preventDefault();
    e.currentTarget.setPointerCapture?.(e.pointerId);
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const pts = [...pointers.current.values()];
    if (pts.length === 2) {
      gesture.current = {
        type: 'pinch',
        startDist: Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y),
        startScale: scale,
      };
    } else if (pts.length === 1) {
      gesture.current = { type: 'pan', startX: e.clientX - pos.x, startY: e.clientY - pos.y, moved: false };
    }
  };

  const onPointerMove = (e) => {
    if (!pointers.current.has(e.pointerId)) return;
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const g = gesture.current;
    if (!g) return;
    const pts = [...pointers.current.values()];
    if (g.type === 'pinch' && pts.length === 2) {
      const dist = Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y);
      applyScale(g.startScale * (dist / (g.startDist || 1)), pos);
    } else if (g.type === 'pan' && scale > 1) {
      g.moved = true;
      setPos({ x: e.clientX - g.startX, y: e.clientY - g.startY });
    }
  };

  const onPointerUp = (e) => {
    pointers.current.delete(e.pointerId);
    if (pointers.current.size === 0) gesture.current = null;
  };

  const onWheel = (e) => {
    e.preventDefault();
    applyScale(scale * (e.deltaY < 0 ? 1.15 : 0.87), pos);
  };

  const onDoubleClick = () => applyScale(scale > 1 ? 1 : 2.5, pos);

  return createPortal(
    <div
      className="fixed inset-0 z-[10001] flex items-center justify-center bg-black/95 backdrop-blur-sm"
      onClick={(e) => { if (e.target === e.currentTarget && scale === 1) onClose(); }}
      data-testid="image-lightbox"
    >
      <button
        onClick={onClose}
        className="absolute right-4 top-4 z-10 rounded-full bg-white/10 p-2 text-white hover:bg-white/20"
        aria-label="Close"
        data-testid="image-lightbox-close-button"
      >
        <X className="h-5 w-5" />
      </button>
      <img
        src={src}
        alt={alt}
        draggable={false}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onWheel={onWheel}
        onDoubleClick={onDoubleClick}
        className="max-h-[92vh] max-w-[96vw] select-none object-contain"
        style={{
          transform: `translate(${pos.x}px, ${pos.y}px) scale(${scale})`,
          transition: gesture.current ? 'none' : 'transform 0.15s ease-out',
          touchAction: 'none',
          cursor: scale > 1 ? 'grab' : 'zoom-in',
        }}
        data-testid="image-lightbox-image"
      />
    </div>,
    document.body,
  );
};
